// Compress an image file in the browser and return a WEBP Blob.
// quality: 0-1 (default 0.82 for cover, 0.7 for content).
// maxWidth: scale down if larger.

// Turn an arbitrary filename/title into a clean, hyphenated, attribution-prefixed
// slug suitable for a downloaded asset (e.g. "Wallet Risk flow.png" → "Scott-Cunningham-Wallet-Risk-Flow").
export function slugifyAssetName(raw: string, prefix = "Scott-Cunningham"): string {
  const base = raw
    .replace(/\.[^.]+$/, "")
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-zA-Z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const titled =
    base
      .split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("-") || "Image";
  return `${prefix}-${titled}`;
}

export async function compressToWebp(
  file: File,
  opts: { quality?: number; maxWidth?: number } = {},
): Promise<{ blob: Blob; filename: string }> {
  const { quality = 0.8, maxWidth = 2000 } = opts;

  // Skip compression for SVG/GIF (animations etc) — just rename if needed.
  if (file.type === "image/svg+xml" || file.type === "image/gif") {
    const ext = file.name.match(/\.[^.]+$/)?.[0] ?? "";
    return { blob: file, filename: `${slugifyAssetName(file.name)}${ext}` };
  }

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    const ext = file.name.match(/\.[^.]+$/)?.[0] ?? "";
    return { blob: file, filename: `${slugifyAssetName(file.name)}${ext}` };
  }

  const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    const ext = file.name.match(/\.[^.]+$/)?.[0] ?? "";
    return { blob: file, filename: `${slugifyAssetName(file.name)}${ext}` };
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("encode failed"))),
      "image/webp",
      quality,
    ),
  );

  return { blob, filename: `${slugifyAssetName(file.name)}.webp` };
}
